/**
 * Module 7: Structure Sets You Free
 *
 * In which Daria brings seventeen points, and Theron tells the story he would
 * not tell.
 * Primary character: Theron | Supporting: Xenia, Philemon, Daria
 *
 * REWORKED 2026-10-04 to the six rules Evening 1 set
 * (docs/CONTENT_REVIEW_2026-10-04.md).
 *
 *   What happens: it is the eve of the gathering. Daria arrives with seventeen
 *   points and comes apart. Xenia looks at Theron, and he tells the Carthage
 *   story he refused to tell on the first evening. It gives Daria the
 *   merchant's question, and from there she gets to three words on the back of
 *   her hand and a first and a last sentence.
 *
 *   Who is wrong: Daria. All seventeen points are true, each was given to her
 *   by somebody on the harbour, and leaving any out feels to her like a kind of
 *   lie. Theron's crate does not persuade her. She writes the three words and
 *   still walks out of the gate with all three tablets under her arm.
 *
 *   The one technique: three anchor words where you can see them, and the first
 *   and last sentence written out word for word. Tool: message-architect.
 *
 * CONTINUITY this evening must keep:
 *   - It pays off Evening 1 ("You tell the Carthage story where the big man
 *     laughs and buys double. There is an earlier one." / "There is not." /
 *     "You were twenty-six."). The earlier one: at twenty-six Theron took
 *     seventeen points to a Carthaginian grain merchant, talked for an hour,
 *     and the man looked at him with pity.
 *   - Theron tells it but does NOT take back "a man can simply decide". Asked
 *     whether he was frightened, he says he was badly prepared, and Xenia lets
 *     it stand. Evening 8 is where he takes it back. Do not resolve it here.
 *   - "Levy. Change. Grandmother." and "She isn't point twelve. She's the
 *     ending." are quoted word for word. Evening 8 has Daria look at the back
 *     of her hand, and the grandmother as the ending of her speech.
 *   - Daria already writes on her hands (Evening 4: "I'm going to write that
 *     on my other hand"), and rocks on her feet when frightened (Evening 6).
 *   - We never hear her last sentence, or the grandmother's story. Evening 8
 *     owns what happened at the gathering.
 *   - Evening 3: Xenia told Theron to prepare one story for the gathering.
 *     Tonight ends on her asking which Carthage it is, and on the back of his
 *     hand, which is still blank. Evening 8 answers it (he tells both).
 *   - Details other evenings now quote: forty jars of wine; "The wax tablet is
 *     back against her chest"; both of Daria's hands "written on, to the
 *     wrist"; she wets her thumb to clear a space (Evening 8 has her do it
 *     again).
 *   - Theron's "They are the headlands you steer by. They are not the sea."
 *     stays. Do not bring back "a map of the territory".
 *
 * Every study below was checked against its abstract or record on 2026-10-04;
 * see docs/audits/finding-your-voice-2026-10/register-modules-7-8-story.md.
 * The Murdock box keeps its caveat ("a fair guess and not a finding") wherever
 * it goes. The three frameworks are teaching devices, not findings, and live
 * only in the notes.
 */

export const MODULE_07_CONTENT = [
  // --- The eve: seventeen points ---
  {
    type: 'narration',
    text: 'It is the eve of the gathering. Borrowed benches are stacked by the gate, lamps are going up along the walls, and Xenia puts one end of a bay garland into your hands before you can sit. Philemon has the other end. Theron has a shipping crate. He has sat on it since noon, waiting to be asked about it.',
  },
  {
    type: 'narration',
    text: 'Daria comes through the gate at speed. The wax tablet is back against her chest, and it has been joined by two more.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'Theron. Theron. I have seventeen points.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Seventeen.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'I keep finding more. The trade routes, the weighing fee, the mooring dues, my grandmother. I don\'t know which one comes first.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Then cut fourteen.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'Which fourteen? They\'re all true. The rope-seller\'s wife told me about the mooring dues herself. If I leave it out I\'ve left her out, and that\'s a kind of lie, and tomorrow I stand up in front of everyone and I...',
  },
  {
    type: 'narration',
    text: 'She is rocking on her feet again. Xenia looks at Theron, and goes on looking.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'No.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Seventeen, Theron.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'I heard the number.',
  },
  {
    type: 'narration',
    text: 'Theron looks into his cup, the way he did the night Xenia first said Carthage, and sets it down on the stone.',
  },

  // --- Carthage, at twenty-six ---
  {
    type: 'heading',
    text: 'The earlier one',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'I was twenty-six. My first cargo on my own account: forty jars of wine, and every one owed for. I took them to a grain merchant in Carthage, a great slab of a man, and I took seventeen points with them. I had practised them on the helmsman, who could not leave.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'I talked for an hour. The hillside, the vintage, the pitch on the jars, the good name of my house. Every time I stopped, he might have answered. So I did not stop.',
  },
  {
    type: 'narration',
    text: 'His hands, which are never still, have not left his knees.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'When I had nothing left, he looked at me. I have been shouted at in every harbour on this sea, and shouting I can use. This was pity. From a Carthaginian. He said: "What do you actually want?"',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'What did you want?',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'For him to buy forty jars of wine. Seventeen points, and not one of them was that. I worked it out three days from home, with the hold still full. The one where he laughs and buys double is the spring after. I tell that one.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'You were frightened.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'I was badly prepared.',
  },
  {
    type: 'narration',
    text: 'The garland has gone slack between you and Philemon, who was told on the first evening that a man can simply decide, and says nothing. Xenia fills Theron\'s cup and lets his answer stand, as a creditor lets a debt stand. Daria has stopped rocking.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Now you. What do you want from them tomorrow?',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'The levy changed.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Which point is that?',
  },
  {
    type: 'narration',
    text: 'She goes through one tablet, then another.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'Nine.',
  },

  // --- The crate, and the back of a hand ---
  {
    type: 'heading',
    text: 'Three words',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Ask me about the crate.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'No.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Thirty cups crossed three weeks of open water in this, and not one cracked. The potter made more than thirty. The rest are on his shelf, and no worse for it.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'A cup doesn\'t have a rope-seller\'s wife on the third bench, waiting to hear whether she was mentioned.',
  },
  {
    type: 'narration',
    text: 'Theron has no answer for the rope-seller\'s wife. He holds out the back of his hand.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Before any room that matters I put three words here. Not four. I tried four once and spent the whole bargain staring at my own knuckles. When my mind goes white I look down, and the next word tells me where I am. They are the headlands you steer by. They are not the sea.',
  },
  {
    type: 'narration',
    text: 'Daria turns over one hand, then the other. Both are already written on, to the wrist. She wets her thumb and clears a space.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'Levy. Change. Grandmother.',
  },
  {
    type: 'narration',
    text: 'She looks at the third word for a long time.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'She isn\'t point twelve. She\'s the ending.',
  },
  {
    type: 'narration',
    text: 'Theron opens his mouth. Xenia looks at him, and he shuts it. Then his finger swings to you.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Yours. Three words, for the thing you are carrying. Do not tell me they are not ready. Write them where you can see them.',
  },
  {
    type: 'exercise',
    number: 1,
    title: 'Your three words',
    instructions: 'Theron is waiting. Take one thing you have to say this week (a meeting, a toast, a hard conversation) and find its three words.',
    prompts: [
      'First, the merchant\'s question: what do you actually want from the people listening? One plain sentence.',
      'Now three words, one for each part of what you will say, in the order you will say it. Daria\'s were Levy, Change, Grandmother.',
      'Write them where you will see them while you speak: a card, the top of your notes, the back of your hand.',
      'Say the whole thing aloud once from the three words alone. When you lose the thread, look down and take the next word.',
    ],
  },

  // --- The first sentence and the last ---
  {
    type: 'heading',
    text: 'The way in and the way out',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'How do I begin tomorrow? "Thank you all for coming"? "It\'s an honour to be asked"?',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Thank-yous are throat-clearing: the sound a speaker makes while deciding whether to be interesting. Start with the thing itself. Say it to the benches.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'The harbour levy is, in many ways, a matter which...',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'What did it do?',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'Half the stalls on this harbour paid more in levy last season than they earned.',
  },
  {
    type: 'narration',
    text: 'A man carrying a bench across the yard stops to hear the rest.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'And when you stop, what should they carry out through my gate?',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'Her.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Then write those two out tonight, the first sentence and the last, every word. Speak the middle from your hand. Never improvise the last thing they will hear.',
  },
  {
    type: 'research',
    title: 'The first and the last',
    source: 'Murdock, Journal of Experimental Psychology, 1962',
    paragraphs: [
      'In 1962 the psychologist Bennet Murdock gave people lists of words, one at a time, and asked them to recall as many as they could, in any order. They recalled the last words best and the first three or four next best. The middle trailed behind.',
      'A talk is not a list of words, and in later experiments the edge the last words had faded after a short distraction, so the step from the laboratory to a speech is a fair guess and not a finding.',
    ],
  },
  {
    type: 'narration',
    text: 'Xenia takes the garland out of your hands at last, and looks at you while she ties it.',
  },
  {
    type: 'yourTurn',
    id: 'm7-which-door',
    character: 'xenia',
    speaker: 'Xenia',
    prompt: 'And you? Whatever you have to say has a door in and a door out. Which of yours is left to luck, and what happens there?',
    options: [
      {
        label: 'I begin by apologising.',
        reply: {
          character: 'philemon',
          speaker: 'Philemon',
          text: 'So did I, for years. "Forgive me, I am no speaker," said the teacher of rhetoric. Write your first sentence tonight, then strike out everything you would say before it. What is left is where you begin.',
        },
      },
      {
        label: 'I begin with my name and my thanks.',
        reply: {
          character: 'theron',
          speaker: 'Theron',
          text: 'They know your name; somebody told them. Keep the thanks and spend it at the end, where it buys something. Now look at what you usually say third. That is your first sentence.',
        },
      },
      {
        label: 'I trail off.',
        reply: {
          character: 'xenia',
          speaker: 'Xenia',
          text: 'Do you know your last sentence before you stand up? Most who trail off are still hunting for it aloud. Choose it tonight and say it to the wall. Then you will know the way out when you come to it.',
        },
      },
      {
        label: 'I finish with "that\'s all, really".',
        reply: {
          character: 'theron',
          speaker: 'Theron',
          text: 'Then "really" is the last word they hear, and they carry it home. Say your true last sentence. Keep your mouth shut for three heartbeats. Then sit. It will feel like a year, and nobody but you will count it.',
        },
      },
    ],
  },
  {
    type: 'narration',
    text: 'By the time the last lamp is up and Xenia has counted the benches twice, Daria is at the gate with three words on her hand and three tablets under her arm.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Leave the tablets.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'No. And I\'m still afraid, you know.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'So am I. Sit by me tomorrow and we can shake on the same bench.',
  },
  {
    type: 'narration',
    text: 'She laughs, and goes.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'And your one story for tomorrow, Theron. Which Carthage is it?',
  },
  {
    type: 'narration',
    text: 'Theron turns his hand over. There is nothing written on the back of it yet.',
  },

  {
    type: 'divider',
  },

  // --- To take away ---
  {
    type: 'heading',
    text: 'Before tomorrow',
  },
  {
    type: 'exercise',
    number: 2,
    title: 'Two sentences',
    instructions: 'For the next thing you have to say to a room, write two sentences out word for word: the first and the last. Only those two.',
    prompts: [
      'The first: cross out the thanks, the apology and your name. Begin at the sentence that would make a man carrying a bench stop.',
      'The last: write the words you want them to leave with. Decide now what you do after it. Stop, wait three heartbeats, sit down.',
      'Say both aloud until you no longer need the page.',
      'Speak the middle from your three words, and leave the two sentences exactly as written.',
    ],
  },
  {
    type: 'toolLink',
    toolId: 'message-architect',
    title: 'Message Architect',
    text: 'Theron\'s tool builds the rest. You choose one of four shapes for the message, set down your three anchor words, write the sentences that join them, then your first and last sentence word for word. It keeps what you write.',
  },

  // --- The research, for whoever wants it ---
  {
    type: 'notes',
    title: 'What we know now',
    intro: 'Theron can only tell you what thirty years of harbours taught him. Here is what has been tested since, and what has not.',
    items: [
      {
        title: 'Tell them first what it is about',
        paragraphs: [
          'In 1972 John Bransford and Marcia Johnson read people a passage written to be vague on purpose: a string of instructions that never said what they were for. Some listeners were told first that it was about washing clothes. Some were told afterwards, and some were never told. Those who knew the topic before they listened rated the passage much easier to understand and recalled about twice as many of its ideas. Being told afterwards did not help.',
          'We think a signpost in a talk does a similar job: listeners who know what to expect can attend to what you are saying instead of working out where you are. That is our reading, not the study\'s. Theron\'s merchant sat through an hour without one.',
        ],
        source: 'Bransford and Johnson, Journal of Verbal Learning and Verbal Behavior, 1972',
      },
      {
        title: 'Ease may colour belief',
        paragraphs: [
          'A 2009 review by Adam Alter and Daniel Oppenheimer found that, across many experiments, what is easy to take in is more readily judged to be true. Those experiments used things like clear print and easy-to-say words, not the shape of a talk, so count it as a nudge and not a promise of trust.',
        ],
        source: 'Alter and Oppenheimer, Personality and Social Psychology Review, 2009',
      },
      {
        title: 'How a thing ends',
        paragraphs: [
          'In 1993 Daniel Kahneman and colleagues had people hold a hand in painfully cold water twice, and most chose to repeat the longer trial, the one that ended slightly less cold. How the discomfort ended counted for more in their judgement than how long it lasted.',
          'That was cold water and not a speech. It points the same way as the word lists, and it is a fair guess of the same kind.',
        ],
        source: 'Kahneman, Fredrickson, Schreiber and Redelmeier, Psychological Science, 1993',
      },
      {
        title: 'Three shapes that coaches hand on',
        paragraphs: [
          'If three words will not come, borrow a shape. Problem, solution, benefit suits pitches, proposals and most attempts to persuade. Past, present, future suits updates and news of a change. What, so what, now what suits briefings and any time you need to be brief; it began as three questions in Terry Borton\'s 1970 book on teaching, Reach, Touch and Teach. Daria\'s Levy, Change, Grandmother is the first of them, though nobody in the courtyard would call it that.',
          'All three are teaching devices handed on by speaking coaches, not research findings, and none works every time. Hold whichever you choose loosely. If you cannot drop a planned part when the room asks for something else, it has become a script, and a script cannot answer the room.',
        ],
      },
    ],
  },

  // --- Module Summary ---
  {
    type: 'summary',
    characterId: 'theron',
    points: [
      'You cannot carry seventeen points into a room. Choose three. The others stay true without being said.',
      'Three anchor words, written where you can see them, tell you where you are when your mind goes white.',
      'Write your first and last sentences out word for word, and speak the middle from the three words.',
    ],
    nextPreview: {
      dialogue: [
        {
          character: 'theron',
          text: 'Bring your three words. And your feet.',
        },
        {
          character: 'xenia',
          text: 'Tomorrow, the courtyard fills.',
        },
      ],
    },
  },
];

export const MODULE_07_EXERCISES = [
  {
    number: 1,
    title: 'Your three words',
    description: 'Find three anchor words for one thing you have to say, and speak from them.',
    xpReward: 20,
  },
  {
    number: 2,
    title: 'Two sentences',
    description: 'Write your first and last sentences out word for word.',
    xpReward: 20,
  },
];

export const MODULE_07_SUMMARY = {
  title: 'Structure Sets You Free',
  keyInsight: 'Carry three words in, and know your first and last sentence.',
  characterQuote: {
    character: 'theron',
    text: 'They are the headlands you steer by. They are not the sea.',
  },
  takeaways: [
    'Choose three points. The others stay true without being said.',
    'Three anchor words tell you where you are when your mind goes white.',
    'Write your first and last sentences out word for word.',
  ],
};
